import ClaireHelpButton from "../components/ClaireHelpButton";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, TrendingDown, Flame, Activity,
  RefreshCw, Database, Sparkles,
  Package, ChevronDown, ChevronRight,
  Instagram, Linkedin, Youtube,
  Radar, ExternalLink, MessageCircle, Bell, Clock,
  Globe, AlertTriangle, CalendarPlus, Award, PhoneCall, Brain,
} from "lucide-react";
import Sidebar from "../components/Sidebar";
import AppHeader from "../components/AppHeader";
import ErrorState from "../components/ErrorState";
import LangToggle from "../components/LangToggle";
import ThemeToggle from "../components/ThemeToggle";
import Logo from "../components/Logo";
import StatCard from "../components/dashboard/StatCard";
// TopIntentNow widget removed in Iter93 (option D) — keeping the dedicated
// B2C Lead Radar as the canonical hot-mentions surface. Import deleted.
import HeaderUserMenu from "../components/HeaderUserMenu";
import TaxonomyQualityCard from "../components/TaxonomyQualityCard";
import ReclassifyTrendCard from "../components/ReclassifyTrendCard";
import SegmentMergeCard from "../components/SegmentMergeCard";
import { getActionMeta } from "../constants/actions";
import { useLang } from "../i18n/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Human labels for the lead's origin tool (source badge on captured cards)
const SOURCE_LABELS = {
  google_places: { label: "Google Maps", bg: "#E8F0FE", fg: "#1A73E8" },
  crm_manual: { label: "Manual", bg: "#F3F4F6", fg: "#6B7280" },
  sunbiz_florida: { label: "Sunbiz FL", bg: "#FEF3C7", fg: "#B45309" },
  lead_gap: { label: "Lead Gap", bg: "#EDE9FE", fg: "#7C3AED" },
  yelp: { label: "Yelp", bg: "#FEE2E2", fg: "#DC2626" },
  instagram_scraper: { label: "Instagram", bg: "#FCE7F3", fg: "#DB2777" },
  linkedin: { label: "LinkedIn", bg: "#E0F2FE", fg: "#0A66C2" },
  samgov: { label: "SAM.gov", bg: "#DCFCE7", fg: "#15803D" },
  leads_finder: { label: "Leads Finder", bg: "#FEF9C3", fg: "#A16207" },
  multistate: { label: "SOS Estadual", bg: "#FFEDD5", fg: "#C2410C" },
  form_submission: { label: "Formulário", bg: "#F0FDFA", fg: "#0F766E" },
  b2c_intent: { label: "B2C Radar", bg: "#EDE9FE", fg: "#6D28D9" },
};
const sourceMeta = (ld) => {
  const key = ld.source || (String(ld.raw_source || "").includes("li_") ? "linkedin" : null);
  return SOURCE_LABELS[key] || { label: key ? key.replace(/_/g, " ") : "CRM", bg: "#F3F4F6", fg: "#6B7280" };
};
const waLink = (ld) => {
  const digits = String(ld.phone_international || ld.phone || "").replace(/\D/g, "");
  return digits.length >= 8 ? `https://wa.me/${digits}` : null;
};
// Claire "perfect match" heuristic — QUENTE + high digital score
const claireMatch = (ld) => (ld.badge === "QUENTE" && (ld.digital_score ?? 0) >= 85);

// BadgeBar component removed — replaced by inline interactive renderer in Distributions section.

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [leadsBySector, setLeadsBySector] = useState(null);
  const [openSectors, setOpenSectors] = useState({});
  const [openBadge, setOpenBadge] = useState(null);
  const [enrichingId, setEnrichingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifCount, setNotifCount] = useState(0);
  const [intelFeed, setIntelFeed] = useState([]);
  const [brief, setBrief] = useState(null);
  const [briefLoading, setBriefLoading] = useState(false);
  const [scriptOpen, setScriptOpen] = useState(false);
  const [scriptLoading, setScriptLoading] = useState(false);
  const [script, setScript] = useState(null);
  const [credits, setCredits] = useState(null);
  const [wsStats, setWsStats] = useState(null);

  const generateSalesScript = async () => {
    setScriptOpen(true);
    setScriptLoading(true);
    try {
      const { data } = await axios.post(`${API}/claire/sales-script`, { lang });
      if (data.ok) setScript(data);
      else { toast.error(data.detail || "Erro"); setScriptOpen(false); }
    } catch (_e) { toast.error("Falha ao gerar script"); setScriptOpen(false); }
    finally { setScriptLoading(false); }
  };
  // Persistently remember whether the user collapsed the "indices" panel.
  const [indicesOpen, setIndicesOpen] = useState(() => {
    try { return window.localStorage.getItem("alp.dash.indicesOpen") !== "0"; } catch (_e) { return true; }
  });
  const toggleIndices = () => {
    setIndicesOpen((v) => {
      const next = !v;
      try { window.localStorage.setItem("alp.dash.indicesOpen", next ? "1" : "0"); } catch (_e) { /* ignore */ }
      return next;
    });
  };
  const { t, lang } = useLang();
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, ls, igs, lis, yts] = await Promise.all([
        axios.get(`${API}/dashboard-stats`),
        axios.get(`${API}/saved-leads-by-sector`),
        axios.get(`${API}/instagram/stats`).catch(() => ({ data: {} })),
        axios.get(`${API}/linkedin/stats`).catch(() => ({ data: {} })),
        axios.get(`${API}/youtube/stats`).catch(() => ({ data: {} })),
      ]);
      setStats({
        ...s.data,
        _instagram: igs.data || {},
        _linkedin: lis.data || {},
        _youtube: yts.data || {},
      });
      setLeadsBySector(ls.data);
      setLastUpdated(new Date());
      // Claire intelligence feed + daily brief (non-blocking)
      axios.get(`${API}/intelligence-feed`).then(({ data }) => setIntelFeed(data.items || [])).catch(() => {});
      axios.get(`${API}/credits/balance`).then(({ data }) => setCredits(data)).catch(() => {});
      loadBrief(false);
      // Notifications = HOT intent (last 24h) + unread reply drafts (if any)
      try {
        const { data: ti } = await axios.get(`${API}/b2c-intent/top-intent-now?limit=20&hours=24`);
        const hotCount = (ti.items || []).filter((m) => (m.intent_score || 0) >= 85).length;
        setNotifCount(hotCount);
      } catch (_e) { /* silent */ }
    } catch (e) {
      setError(e?.message || "Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  const loadBrief = async (force) => {
    setBriefLoading(true);
    try {
      const { data } = await axios.get(`${API}/claire/daily-brief`, { params: { lang, force } });
      setBrief(data.brief);
    } catch (_e) {} finally { setBriefLoading(false); }
  };

  const reenrichLead = async (leadId, leadName) => {
    if (!leadId) return;
    setEnrichingId(leadId);
    try {
      const { data } = await axios.post(`${API}/enrich-lead/${leadId}`);
      if (data.ok) {
        const oldS = data.score?.digital_score;
        const oldB = data.score?.badge;
        toast.success(t("dash.enrich_success", { name: leadName, score: oldS, badge: oldB }));
        // Refresh lead data
        const { data: ls } = await axios.get(`${API}/saved-leads-by-sector`);
        setLeadsBySector(ls);
      } else if (data.reason === "no_website") {
        toast.warning(t("dash.enrich_no_website", { name: leadName }));
      } else {
        toast.warning(t("dash.enrich_failed", { name: leadName, reason: data.reason || "" }));
      }
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404) {
        toast.error(t("dash.enrich_not_found"));
      } else {
        toast.error(t("dash.enrich_failed", { name: leadName, reason: e?.message || "" }));
      }
    } finally {
      setEnrichingId(null);
    }
  };

  useEffect(() => { load(); }, []);

  // Iter152 — Winning Strategy Analytics (small chip on the KPIs row).
  useEffect(() => {
    axios.get(`${API}/winning-strategy/stats?window_days=7`)
      .then(({ data }) => setWsStats(data))
      .catch(() => { /* keep silent — chip just hides */ });
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--al-bg)] text-[var(--al-text)]">
        <Sidebar />
        <div className="ml-[var(--al-sidebar-w,260px)] flex items-center justify-center h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-[#3B82F6]" />
        </div>
      </div>
    );
  }

  if (error || !stats || !leadsBySector) {
    return (
      <div className="min-h-screen bg-[var(--al-bg)] text-[var(--al-text)]">
        <Sidebar />
        <div className="ml-[var(--al-sidebar-w,260px)] px-8 py-16">
          <ErrorState
            testid="dashboard-error"
            title="Não foi possível carregar o Dashboard"
            message={error || "Verifique sua conexão e tente novamente."}
            onRetry={load}
          />
        </div>
      </div>
    );
  }

  const total = stats.totals.saved_leads;

  return (
    <div className="min-h-screen bg-[var(--al-bg)] text-[var(--al-text)]">
      <Sidebar />
      <div className="lg:ml-[var(--al-sidebar-w,260px)]">
        <AppHeader
          testid="dashboard-header"
          title={t("dash.title")}
          subtitle={t("dash.subtitle")}
          logo={<Logo className="w-8 h-8 sm:w-9 sm:h-9 object-contain shrink-0" />}
          mobileVisible={<>
            {credits && (
              <button type="button" onClick={() => navigate("/billing/history")}
                data-testid="header-credits-chip-mobile"
                className={`flex items-center gap-1 px-2 h-9 rounded-lg border text-[12px] font-bold ${
                  credits.balance <= 0
                    ? "border-rose-400 text-rose-500 bg-rose-500/10"
                    : "border-[#C9A24E]/50 text-[#C9A24E] bg-[var(--al-card-bg)]"}`}>
                <Sparkles className="w-3.5 h-3.5" />
                <span className="tabular-nums">{credits.balance.toLocaleString()}</span>
              </button>
            )}
            <button type="button"
              data-testid="dashboard-notifications-mobile"
              onClick={() => setNotifOpen((o) => !o)}
              className="relative w-9 h-9 rounded-lg border border-[var(--al-border)] bg-[var(--al-card-bg)] inline-flex items-center justify-center">
              <Bell className="w-4 h-4" />
              {notifCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-[#EF4444] text-white text-[9px] font-bold flex items-center justify-center">
                  {notifCount > 9 ? "9+" : notifCount}
                </span>
              )}
            </button>
          </>}
          mobileMenu={<>
            <button type="button" onClick={load}
              data-testid="dashboard-refresh-btn-mobile"
              className="w-full flex items-center gap-2 px-3 h-10 rounded-md text-[13px] hover:bg-white/5">
              <RefreshCw className="w-4 h-4" /> {t("dash.refresh")}
            </button>
            <div className="flex items-center gap-1 px-2 py-1.5">
              <ClaireHelpButton testid="dashboard-mobile"
                question="Como leio o Agency Dashboard — KPIs, funil de leads e notificações?" />
              <ThemeToggle />
              <LangToggle />
            </div>
            {lastUpdated && (
              <div className="border-t border-[var(--al-border)] mt-1 pt-2 px-3 pb-1.5 text-[10.5px] text-[var(--al-text-muted)] inline-flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {t("dash.last_updated")}:{" "}
                <strong className="text-[var(--al-text)] tabular-nums">
                  {lastUpdated.toLocaleTimeString()}
                </strong>
              </div>
            )}
          </>}
        >
          {/* Desktop toolbar */}
          <ClaireHelpButton testid="dashboard"
            question="Como leio o Agency Dashboard — KPIs, funil de leads e notificações?" />
          <span data-testid="dashboard-last-updated" className="hidden lg:inline-flex items-center gap-1 text-[11px] text-[var(--al-text-muted)] mr-1.5">
            <Clock className="w-3 h-3" />
            {t("dash.last_updated")}:{" "}
            <strong className="text-[var(--al-text)] tabular-nums">
              {lastUpdated ? lastUpdated.toLocaleTimeString() : "—"}
            </strong>
          </span>
          <ThemeToggle />
          <LangToggle />
          {credits && (
            <button type="button" onClick={() => navigate("/billing/history")}
              data-testid="header-credits-chip"
              title={lang === "pt" ? "Ver histórico e loja de créditos" : "Credits history & store"}
              className={`flex items-center gap-1.5 px-3 h-9 rounded-lg border text-[12.5px] font-bold transition-colors ${
                credits.balance <= 0
                  ? "border-rose-400 text-rose-500 bg-rose-500/10"
                  : "border-[#C9A24E]/50 text-[#C9A24E] bg-[var(--al-card-bg)] hover:border-[#C9A24E]"}`}>
              <Sparkles className="w-3.5 h-3.5" />
              <span className="tabular-nums">{credits.balance.toLocaleString()}</span>
            </button>
          )}
          <button
            data-testid="dashboard-refresh-btn"
            onClick={load}
            className="flex items-center gap-1.5 px-3 h-9 rounded-lg border border-[var(--al-border)] bg-[var(--al-card-bg)] text-[12.5px] font-medium hover:border-[#3B82F6] hover:text-[#3B82F6] transition-colors"
          >
            <RefreshCw className="w-3.5 h-3.5" /> {t("dash.refresh")}
          </button>
          <div className="relative">
            <button
              type="button"
              data-testid="dashboard-notifications"
              onClick={() => setNotifOpen((o) => !o)}
              title={t("dash.notifications")}
              className="relative w-9 h-9 rounded-lg border border-[var(--al-border)] bg-[var(--al-card-bg)] hover:border-[#EF4444] hover:text-[#EF4444] inline-flex items-center justify-center transition-colors"
            >
              <Bell className="w-4 h-4" />
              {notifCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-[#EF4444] text-white text-[9px] font-bold flex items-center justify-center">
                  {notifCount > 9 ? "9+" : notifCount}
                </span>
              )}
            </button>
            {notifOpen && (
              <div data-testid="dashboard-notif-panel"
                className="absolute right-0 mt-2 w-[300px] max-w-[calc(100vw-24px)] rounded-lg border border-[var(--al-border)] bg-[var(--al-card-bg)] shadow-xl p-4 z-40">
                <div className="text-[12px] font-bold text-[var(--al-text)] mb-2 flex items-center gap-1.5">
                  <Bell className="w-3.5 h-3.5 text-[#EF4444]" /> {t("dash.notifications")}
                </div>
                {notifCount === 0 ? (
                  <div className="text-[11.5px] italic text-[var(--al-text-muted)]">
                    {t("dash.notif_empty")}
                    </div>
                  ) : (
                    <div className="text-[12px] leading-relaxed text-[var(--al-text)]">
                      <strong className="text-[#EF4444]">{notifCount}</strong> {t("dash.notif_hot")}{" "}
                      <a href="/b2c-intent" className="text-[#8B5CF6] font-semibold hover:underline">{t("dash.notif_see")} →</a>
                    </div>
                  )}
                </div>
              )}
            </div>
            <HeaderUserMenu />
        </AppHeader>

        <main className="px-3 sm:px-6 lg:px-8 py-4 sm:py-6 max-w-[1400px] mx-auto space-y-4 sm:space-y-5 pb-24 lg:pb-6">
          {/* Courtesy credits welcome banner + usage progress */}
          {credits?.courtesy && credits.balance > 0 && (
            <section className="rounded-xl p-4 border border-[#C9A24E]/40 bg-gradient-to-r from-[#C9A24E]/15 to-transparent flex items-center gap-4" data-testid="courtesy-banner">
              <span className="text-[26px]">🎁</span>
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-bold text-[var(--al-text)]">
                  {lang === "pt"
                    ? `Bem-vindo! Você recebeu ${credits.starting_balance} créditos de cortesia — restam ${credits.balance}.`
                    : `Welcome! You received ${credits.starting_balance} courtesy credits — ${credits.balance} left.`}
                </p>
                <p className="text-[11.5px] text-[var(--al-text-muted)]">
                  {lang === "pt"
                    ? "Use-os agora para analisar um edital do governo ou enriquecer seus leads!"
                    : "Use them now to analyze a government bid or enrich your leads!"}
                </p>
                <div className="mt-1.5 h-1.5 max-w-[280px] bg-[var(--al-border)] rounded-full overflow-hidden">
                  <div className="h-full bg-[#C9A24E] rounded-full transition-all"
                    style={{ width: `${(credits.balance / (credits.starting_balance || 50)) * 100}%` }} />
                </div>
              </div>
              <button type="button" onClick={() => navigate("/gov-kanban")}
                data-testid="courtesy-cta"
                className="h-9 px-3.5 rounded-lg bg-[#C9A24E] text-[#1A1A2E] text-[11.5px] font-extrabold hover:brightness-110 shrink-0">
                {lang === "pt" ? "Analisar editais ⭐" : "Analyze bids ⭐"}
              </button>
            </section>
          )}

          {/* Claire's Daily Brief — AI summary of the day (purple = intelligence) */}
          <section className="rounded-xl p-5 border border-[#8B5CF6]/30 bg-gradient-to-r from-[#8B5CF6]/10 via-[#8B5CF6]/5 to-transparent" data-testid="claire-daily-brief">
            <div className="flex items-start gap-3.5">
              <div className="w-10 h-10 rounded-full bg-[#8B5CF6] text-white flex items-center justify-center shrink-0 shadow-lg shadow-[#8B5CF6]/30">
                <Sparkles className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-[13.5px] font-bold text-[var(--al-text)]">Claire's Daily Brief</h3>
                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-[#8B5CF6] text-white uppercase tracking-wide">AI</span>
                  <button type="button" onClick={() => loadBrief(true)} disabled={briefLoading}
                    data-testid="brief-refresh"
                    title={lang === "pt" ? "Gerar novo resumo" : "Regenerate"}
                    className="ml-auto text-[#8B5CF6] hover:text-[#6D28D9] disabled:opacity-50">
                    {briefLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                  </button>
                </div>
                <p className="text-[13px] leading-relaxed text-[var(--al-text)]" data-testid="brief-text">
                  {brief || (briefLoading
                    ? (lang === "pt" ? "Claire está analisando o dia..." : "Claire is analyzing your day...")
                    : (lang === "pt" ? "Sem resumo ainda — clique em atualizar." : "No brief yet — click refresh."))}
                </p>
                <div className="mt-2.5 flex flex-col sm:flex-row sm:items-center gap-2">
                  <button type="button" onClick={generateSalesScript}
                    data-testid="brief-sales-script-btn"
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 h-10 sm:h-8 px-3 rounded-lg bg-[#8B5CF6] text-white text-[12.5px] sm:text-[11.5px] font-bold hover:bg-[#6D28D9] transition-colors">
                    <Sparkles className="w-3.5 h-3.5" />
                    {lang === "pt" ? "Gerar Script de Vendas" : "Generate Sales Script"}
                  </button>
                  <button type="button" onClick={() => navigate("/gov-kanban")}
                    data-testid="brief-gov-btn"
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 h-10 sm:h-8 px-3 rounded-lg border border-[#8B5CF6]/40 text-[#8B5CF6] text-[12.5px] sm:text-[11.5px] font-bold hover:bg-[#8B5CF6]/10 transition-colors">
                    {lang === "pt" ? "Ver oportunidades ⭐" : "View opportunities ⭐"}
                  </button>
                </div>
              </div>
            </div>
            {/* Sales script modal */}
            {scriptOpen && (
              <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4"
                onClick={() => setScriptOpen(false)} data-testid="sales-script-modal">
                <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-y-auto p-5"
                  onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="w-4 h-4 text-[#8B5CF6]" />
                    <h3 className="text-[14px] font-bold text-[#111827] flex-1 truncate">
                      {scriptLoading
                        ? (lang === "pt" ? "Claire está escrevendo..." : "Claire is writing...")
                        : (script?.target?.title || "Script de Vendas")}
                    </h3>
                    {script?.target?.victory_index != null && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#DCFCE7] text-[#15803D]">
                        ⭐ {script.target.victory_index}/10
                      </span>
                    )}
                  </div>
                  {scriptLoading ? (
                    <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin inline text-[#8B5CF6]" /></div>
                  ) : (
                    <>
                      <pre className="whitespace-pre-wrap text-[12px] text-[#374151] bg-[#F9FAFB] rounded-lg p-3 border border-[#E5E7EB]" data-testid="sales-script-text">
                        {script?.script}
                      </pre>
                      <div className="mt-3 flex gap-2">
                        <button type="button"
                          onClick={() => { navigator.clipboard.writeText(script?.script || ""); toast.success(lang === "pt" ? "Copiado!" : "Copied!"); }}
                          data-testid="sales-script-copy"
                          className="flex-1 h-9 rounded-lg bg-[#8B5CF6] text-white text-[12px] font-bold hover:bg-[#6D28D9]">
                          {lang === "pt" ? "Copiar script" : "Copy script"}
                        </button>
                        <button type="button" onClick={() => setScriptOpen(false)}
                          className="h-9 px-4 rounded-lg border border-[#E5E7EB] text-[12px] font-semibold text-[#6B7280] hover:bg-[#F9FAFB]">
                          {lang === "pt" ? "Fechar" : "Close"}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </section>

          {/* Intelligence Feed — Claire's 5 latest events */}
          {intelFeed.length > 0 && (
            <section className="al-card p-0 overflow-hidden" data-testid="intelligence-feed">
              <div className="px-5 py-3 border-b border-[var(--al-border)] flex items-center gap-2">
                <Radar className="w-4 h-4 text-[#8B5CF6]" />
                <h3 className="text-[13px] font-bold text-[var(--al-text)]">
                  {lang === "pt" ? "Intelligence Feed — o que a Claire encontrou" : "Intelligence Feed — what Claire found"}
                </h3>
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-[#8B5CF6]/15 text-[#8B5CF6] uppercase">Live</span>
              </div>
              <div className="divide-y divide-[var(--al-border)]">
                {intelFeed.map((ev, i) => (
                  <button key={`${ev.type}-${i}`} type="button"
                    onClick={() => navigate(ev.route)}
                    data-testid={`intel-item-${i}`}
                    className="w-full flex items-center gap-3 px-5 py-2.5 text-left hover:bg-[#8B5CF6]/5 transition-colors">
                    <span className="flex-1 text-[12.5px] text-[var(--al-text)] truncate">
                      {lang === "pt" ? ev.text_pt : ev.text_en}
                    </span>
                    <span className="text-[10px] text-[var(--al-text-muted)] shrink-0 tabular-nums">
                      {(ev.ts || "").slice(5, 16).replace("T", " ")}
                    </span>
                    <ExternalLink className="w-3 h-3 text-[#8B5CF6] shrink-0" />
                  </button>
                ))}
              </div>
            </section>
          )}

          {/* Work-start hero: today's capture + week qualification + action required */}
          {stats.work_kpis && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="work-kpis">
              <div className="al-card p-5 flex items-center gap-4 border-l-4 border-[#3B82F6]" data-testid="kpi-captured-today">
                <div className="w-11 h-11 rounded-xl bg-[#EFF6FF] text-[#3B82F6] flex items-center justify-center shrink-0">
                  <CalendarPlus className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-[30px] font-extrabold leading-none tabular-nums text-[var(--al-text)]">{stats.work_kpis.captured_today}</div>
                  <div className="text-[11.5px] text-[var(--al-text-muted)] mt-1 font-medium">
                    {lang === "pt" ? "Leads capturados hoje" : "Leads captured today"}
                  </div>
                </div>
              </div>
              <div className="al-card p-5 flex items-center gap-4 border-l-4 border-[#EF4444]" data-testid="kpi-qualified-week">
                <div className="w-11 h-11 rounded-xl bg-[#FEE2E2] text-[#EF4444] flex items-center justify-center shrink-0">
                  <Award className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-[30px] font-extrabold leading-none tabular-nums text-[var(--al-text)]">{stats.work_kpis.qualified_week}</div>
                  <div className="text-[11.5px] text-[var(--al-text-muted)] mt-1 font-medium">
                    {lang === "pt" ? "Qualificados (QUENTE) esta semana" : "Qualified (HOT) this week"}
                  </div>
                </div>
              </div>
              <button type="button" onClick={() => navigate("/kanban")}
                data-testid="kpi-action-required"
                className="al-card p-5 flex items-center gap-4 border-l-4 border-[#F59E0B] text-left hover:shadow-md transition-shadow cursor-pointer">
                <div className="w-11 h-11 rounded-xl bg-[#FEF3C7] text-[#B45309] flex items-center justify-center shrink-0">
                  <AlertTriangle className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-[30px] font-extrabold leading-none tabular-nums text-[var(--al-text)]">{stats.work_kpis.not_moved}</div>
                  <div className="text-[11.5px] text-[#B45309] mt-1 font-bold">
                    {lang === "pt" ? "Ação requerida: sem movimento no CRM →" : "Action required: not moved in CRM →"}
                  </div>
                </div>
              </button>
            </div>
          )}

          {/* KPI cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              testid="kpi-saved-leads"
              label={t("dash.kpi.saved")}
              value={total}
              sublabel={t("dash.kpi.saved_sub")}
              icon={Database} color="#3B82F6"
              href="/log?action=save"
              tooltip={t("dash.kpi_link_tooltip")}
            />
            <StatCard
              testid="kpi-avg-score"
              label={t("dash.kpi.score")}
              value={stats.totals.avg_score}
              sublabel={t("dash.kpi.score_sub")}
              icon={TrendingDown} color="#F59E0B"
            />
            <StatCard
              testid="kpi-quente"
              label={t("dash.kpi.hot")}
              value={stats.by_badge.QUENTE}
              sublabel={total ? `${((stats.by_badge.QUENTE / total) * 100).toFixed(0)}% ${lang === "pt" ? "do CRM" : "of CRM"}` : t("dash.kpi.hot_sub_zero")}
              icon={Flame} color="#EF4444"
              href="/"
              tooltip={t("dash.kpi_link_tooltip")}
            />
            <StatCard
              testid="kpi-activity"
              label={t("dash.kpi.activity")}
              value={stats.totals.activity_count}
              sublabel={t("dash.kpi.activity_sub")}
              icon={Activity} color="#8B5CF6"
              href="/log"
              tooltip={t("dash.kpi_link_tooltip")}
            />
          </div>

          {/* Top Intent Now — REMOVED (Iter93, option D per master request).
              Dedicated B2C Lead Radar already covers this; the dashboard
              widget was confusing when empty. */}

          {/* Iter331 · Taxonomy Quality — surfaces unclassified leads and
              offers AI suggestions to reclassify. */}
          <TaxonomyQualityCard />

          {/* Iter338 · 30-day reclassification trend by segment. */}
          <ReclassifyTrendCard />

          {/* Iter339 · Segment Merge Wizard — hidden when no duplicates. */}
          <SegmentMergeCard />

          {/* Winning Strategy Analytics — Iter152 */}
          {wsStats && (
            <a
              href="/gov-kanban"
              data-testid="ws-analytics-chip"
              className="group flex items-center gap-4 rounded-2xl border border-fuchsia-500/25 bg-gradient-to-br from-fuchsia-500/10 via-purple-500/5 to-transparent px-5 py-4 hover:border-fuchsia-400/60 transition-colors"
              title={lang === "pt" ? "Ver estratégias no Gov Kanban" : "See strategies in Gov Kanban"}
            >
              <div className="w-11 h-11 rounded-xl bg-fuchsia-500/15 flex items-center justify-center ring-1 ring-fuchsia-400/30 shrink-0">
                <Brain className="w-5 h-5 text-fuchsia-300" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="text-[24px] font-extrabold text-[var(--al-text)]" data-testid="ws-analytics-count">
                    {wsStats.window?.total ?? 0}
                  </span>
                  <span className="text-[11px] uppercase tracking-widest font-bold text-fuchsia-400">
                    {lang === "pt" ? "Winning Strategies · últimos 7 dias" : "Winning Strategies · last 7 days"}
                  </span>
                </div>
                <div className="text-[11.5px] text-[var(--al-text-muted)] mt-0.5">
                  {lang === "pt"
                    ? `${wsStats.window?.gov || 0} Gov · ${wsStats.window?.b2b || 0} B2B · ${wsStats.all_time?.total || 0} no total`
                    : `${wsStats.window?.gov || 0} Gov · ${wsStats.window?.b2b || 0} B2B · ${wsStats.all_time?.total || 0} all-time`}
                </div>
              </div>
              <ExternalLink className="w-4 h-4 text-fuchsia-300 opacity-0 group-hover:opacity-100 transition" />
            </a>
          )}

          {/* Scraper KPIs (Instagram + LinkedIn + YouTube) */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="dash-scraper-kpis">
            <StatCard
              testid="kpi-instagram"
              label={lang === "pt" ? "Instagram · Posts" : "Instagram · Posts"}
              value={(stats._instagram && stats._instagram.total_posts) || 0}
              sublabel={lang === "pt"
                ? `${(stats._instagram && stats._instagram.posts_with_email) || 0} com e-mail`
                : `${(stats._instagram && stats._instagram.posts_with_email) || 0} with email`}
              icon={Instagram} color="#DD2A7B"
              href="/instagram"
              tooltip={lang === "pt" ? "Abrir Instagram Scraper" : "Open Instagram Scraper"}
            />
            <StatCard
              testid="kpi-linkedin"
              label={lang === "pt" ? "LinkedIn · Leads" : "LinkedIn · Leads"}
              value={(stats._linkedin && stats._linkedin.total_leads) || 0}
              sublabel={lang === "pt"
                ? `${(stats._linkedin && stats._linkedin.leads_with_email) || 0} com e-mail`
                : `${(stats._linkedin && stats._linkedin.leads_with_email) || 0} with email`}
              icon={Linkedin} color="#0A66C2"
              href="/linkedin"
              tooltip={lang === "pt" ? "Abrir LinkedIn Scraper" : "Open LinkedIn Scraper"}
            />
            <StatCard
              testid="kpi-youtube"
              label={lang === "pt" ? "YouTube · Vídeos" : "YouTube · Videos"}
              value={(stats._youtube && stats._youtube.total_videos) || 0}
              sublabel={lang === "pt"
                ? `${(stats._youtube && stats._youtube.unique_channels) || 0} canais`
                : `${(stats._youtube && stats._youtube.unique_channels) || 0} channels`}
              icon={Youtube} color="#FF0000"
              href="/youtube"
              tooltip={lang === "pt" ? "Abrir YouTube Scraper" : "Open YouTube Scraper"}
            />
          </div>

          {/* Lead Conversion Funnel — REMOVED (Iter93, option D per master
              request). The funnel only populates after multiple workflow
              actions are tracked; in practice it was always empty for new
              users and added clutter. The detailed by-action breakdown is
              still available inside the collapsible "Dashboard indices"
              panel below (Activity by type). */}

          {/* Distributions — collapsible "Índices do Dashboard" panel */}
          <section className="al-card p-0 overflow-hidden" data-testid="dash-indices-panel">
            <button
              type="button"
              onClick={toggleIndices}
              data-testid="dash-indices-toggle"
              aria-expanded={indicesOpen}
              className="w-full flex items-center justify-between gap-3 px-5 py-3.5 hover:bg-[#FAFAFB] transition-colors text-left border-b border-[#E5E7EB]"
            >
              <div className="flex items-center gap-3 min-w-0">
                {indicesOpen ? <ChevronDown className="w-4 h-4 text-[#6B7280] shrink-0" /> : <ChevronRight className="w-4 h-4 text-[#6B7280] shrink-0" />}
                <div>
                  <h3 className="text-[14px] font-bold text-[#111827]">
                    {lang === "pt" ? "Índices do Dashboard" : "Dashboard indices"}
                  </h3>
                  <p className="text-[11.5px] text-[#6B7280] mt-0.5">
                    {lang === "pt"
                      ? "Distribuição por temperatura, top setores e atividade recente — clique para recolher/expandir."
                      : "Temperature split, top sectors and recent activity — click to collapse/expand."}
                  </p>
                </div>
              </div>
              <span className="text-[11.5px] text-[#3B82F6] font-medium shrink-0">
                {indicesOpen
                  ? (lang === "pt" ? "Recolher" : "Collapse")
                  : (lang === "pt" ? "Expandir" : "Expand")}
              </span>
            </button>

            {indicesOpen && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 p-5" data-testid="dash-indices-content">
            <section className="al-card p-5 lg:col-span-1">
              <h3 className="text-[14px] font-bold text-[#111827] mb-4">{t("dash.distrib_title")}</h3>
              <div className="space-y-2" data-testid="badge-distribution">
                {[
                  { key: "QUENTE", count: stats.by_badge.QUENTE, badgeClass: "al-badge-quente", color: "#EF4444" },
                  { key: "MORNO",  count: stats.by_badge.MORNO,  badgeClass: "al-badge-morno",  color: "#F97316" },
                  { key: "FRIO",   count: stats.by_badge.FRIO,   badgeClass: "al-badge-frio",   color: "#22C55E" },
                ].map((b) => {
                  const pct = total > 0 ? (b.count / total) * 100 : 0;
                  const isOpen = openBadge === b.key;
                  // Flatten leads from all sectors, filter by badge
                  const filteredLeads = leadsBySector.groups
                    .flatMap((g) => g.leads || [])
                    .filter((ld) => (ld.badge || "MORNO") === b.key);
                  return (
                    <div key={b.key}>
                      <button
                        type="button"
                        data-testid={`badge-toggle-${b.key}`}
                        onClick={() => setOpenBadge(isOpen ? null : b.key)}
                        disabled={b.count === 0}
                        className={
                          "w-full text-left rounded-lg p-2 -mx-2 transition-colors " +
                          (b.count === 0
                            ? "cursor-not-allowed opacity-60"
                            : "hover:bg-[#FAFAFB] cursor-pointer")
                        }
                      >
                        <div className="flex items-center justify-between text-[12.5px] mb-1.5">
                          <span className={`al-badge ${b.badgeClass} flex items-center gap-1.5`}>
                            <span className="w-1.5 h-1.5 rounded-full" style={{ background: b.color }} /> {b.key}
                            {b.count > 0 && (
                              <span className="ml-1 text-[9.5px] opacity-80">{isOpen ? "▼" : "▶"}</span>
                            )}
                          </span>
                          <span className="text-[#111827] font-semibold">
                            {b.count} <span className="text-[#9CA3AF] font-normal">· {pct.toFixed(0)}%</span>
                          </span>
                        </div>
                        <div className="h-1.5 bg-[#F3F4F6] rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: b.color }} />
                        </div>
                      </button>
                      {isOpen && filteredLeads.length > 0 && (
                        <div
                          data-testid={`badge-leads-${b.key}`}
                          className="mt-2 mb-3 max-h-[280px] overflow-y-auto pr-1 space-y-1.5 border-l-2 pl-3"
                          style={{ borderColor: b.color }}
                        >
                          {filteredLeads.map((ld, idx) => (
                            <div
                              key={`${b.key}-${ld.id || ld.name}-${idx}`}
                              className="text-[11.5px] text-[#374151] flex items-center justify-between gap-2 py-1 border-b border-[#F3F4F6] last:border-b-0"
                            >
                              <div className="min-w-0">
                                <div className="font-semibold text-[#111827] truncate">{ld.name}</div>
                                <div className="text-[10.5px] text-[#6B7280] truncate">{ld.sector || "—"} · {ld.address || ""}</div>
                              </div>
                              <span className="font-bold text-[11px] shrink-0" style={{ color: b.color }}>
                                {ld.digital_score ?? "—"}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {total === 0 && (
                <p className="text-[12px] text-[#9CA3AF] mt-4">
                  {t("dash.distrib_empty")}
                </p>
              )}
            </section>

            <section className="al-card p-5 lg:col-span-1">
              <h3 className="text-[14px] font-bold text-[#111827] mb-4">{t("dash.top_sectors")}</h3>
              {stats.top_sectors.length === 0 ? (
                <p className="text-[12px] text-[#9CA3AF]">{t("dash.no_data")}</p>
              ) : (
                <div className="space-y-1.5" data-testid="top-sectors-list">
                  {stats.top_sectors.map((s, sIdx) => {
                    const pct = total > 0 ? (s.count / total) * 100 : 0;
                    const isOpen = !!openSectors[`top-${s.sector}`];
                    const sectorGroup = leadsBySector.groups.find((g) => g.sector === s.sector);
                    const leads = sectorGroup?.leads || [];
                    return (
                      <div key={`${s.sector}-${sIdx}`}>
                        <button
                          type="button"
                          data-testid={`top-sector-toggle-${s.sector}`}
                          onClick={() => setOpenSectors((o) => ({ ...o, [`top-${s.sector}`]: !o[`top-${s.sector}`] }))}
                          disabled={leads.length === 0}
                          className={
                            "w-full text-left rounded-lg p-1.5 -mx-1.5 transition-colors " +
                            (leads.length === 0
                              ? "cursor-default"
                              : "hover:bg-[#FAFAFB] cursor-pointer")
                          }
                        >
                          <div className="flex items-center justify-between text-[12.5px] mb-1">
                            <span className="font-medium text-[#111827] flex items-center gap-1.5">
                              {s.sector}
                              {leads.length > 0 && (
                                <span className="text-[9.5px] text-[#9CA3AF]">{isOpen ? "▼" : "▶"}</span>
                              )}
                            </span>
                            <span className="text-[#6B7280]">{s.count}</span>
                          </div>
                          <div className="h-1.5 bg-[#F3F4F6] rounded-full overflow-hidden">
                            <div className="h-full bg-[#3B82F6] rounded-full" style={{ width: `${pct}%` }} />
                          </div>
                        </button>
                        {isOpen && leads.length > 0 && (
                          <div
                            data-testid={`top-sector-leads-${s.sector}`}
                            className="mt-1.5 mb-2 max-h-[180px] overflow-y-auto pr-1 space-y-1 border-l-2 border-[#3B82F6] pl-3"
                          >
                            {leads.slice(0, 10).map((ld, idx) => (
                              <div
                                key={`${s.sector}-${ld.id || ld.name}-${idx}`}
                                className="text-[11px] text-[#374151] flex items-center justify-between gap-2 py-0.5"
                              >
                                <span className="truncate font-medium text-[#111827]">{ld.name}</span>
                                <span className="text-[10.5px] text-[#6B7280] shrink-0">{ld.badge || "MORNO"} · {ld.digital_score ?? "—"}</span>
                              </div>
                            ))}
                            {leads.length > 10 && (
                              <div className="text-[10px] text-[#9CA3AF] italic pt-1">+{leads.length - 10} more</div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            <section className="al-card p-5 lg:col-span-1">
              <h3 className="text-[14px] font-bold text-[#111827] mb-4">{t("dash.activity_by_type")}</h3>
              {Object.keys(stats.by_action).length === 0 ? (
                <p className="text-[12px] text-[#9CA3AF]">{t("dash.no_records")}</p>
              ) : (
                <div className="space-y-1" data-testid="activity-by-type-list">
                  {Object.entries(stats.by_action).map(([k, v]) => {
                    const meta = getActionMeta(k);
                    const ActionIcon = meta.icon;
                    return (
                      <a
                        key={k}
                        href={`/log?action=${encodeURIComponent(k)}`}
                        data-testid={`activity-type-link-${k}`}
                        title={t("dash.activity_link_tooltip", { label: meta.label })}
                        className="flex items-center justify-between py-2 px-2 -mx-2 rounded-md border-b border-[#F3F4F6] last:border-b-0 hover:bg-[#FAFAFB] transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: `${meta.color}1A`, color: meta.color }}>
                            <ActionIcon className="w-3.5 h-3.5" />
                          </div>
                          <span className="text-[13px] font-medium text-[#111827]">{meta.label}</span>
                        </div>
                        <span className="text-[13px] font-semibold text-[#6B7280]">{v}</span>
                      </a>
                    );
                  })}
                </div>
              )}
            </section>
              </div>
            )}
          </section>

          {/* Captured leads by business type */}
          <section className="al-card p-0 overflow-hidden" data-testid="captured-leads-section">
            <div className="px-5 py-4 border-b border-[#E5E7EB] flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-lg bg-[#FEF3C7] text-[#B45309] flex items-center justify-center">
                <Package className="w-4 h-4" />
              </div>
              <div>
                <h3 className="text-[14px] font-bold text-[#111827]">
                  {t("dash.captured_title")}
                </h3>
                <p className="text-[12px] text-[#6B7280] mt-0.5">
                  {t("dash.captured_subtitle")}
                  {leadsBySector.count > 0 && (
                    <> · {leadsBySector.groups.reduce((s, g) => s + g.count, 0)} leads {lang === "pt" ? "em" : "in"} {leadsBySector.count} {lang === "pt" ? "setores" : "sectors"}</>
                  )}
                </p>
              </div>
              <button type="button" data-testid="manage-sources-btn"
                onClick={() => navigate("/settings/sources")}
                className="ml-auto inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-[#C9A24E] text-[#92660C] text-[12px] font-bold hover:bg-[#C9A24E] hover:text-[#0A1233] transition-colors shrink-0">
                <Database className="w-3.5 h-3.5" />
                {lang === "pt" ? "Gerenciar Fontes" : "Manage Sources"}
              </button>
            </div>

            {leadsBySector.count === 0 ? (
              <div className="px-5 py-10 text-center text-[13px] text-[#6B7280]">
                {t("dash.captured_empty")}
              </div>
            ) : (
              <div className="divide-y divide-[#F3F4F6]">
                {(() => {
                  const maxCount = Math.max(...leadsBySector.groups.map((x) => x.count), 1);
                  return leadsBySector.groups.map((g, gIdx) => {
                  const open = !!openSectors[g.sector];
                  const ChevIcon = open ? ChevronDown : ChevronRight;
                  const barPct = (g.count / maxCount) * 100;
                  return (
                    <div key={`${g.sector}-${gIdx}`} data-testid={`captured-group-${g.sector}`}>
                      <button
                        type="button"
                        data-testid={`captured-toggle-${g.sector}`}
                        onClick={() => setOpenSectors((s) => ({ ...s, [g.sector]: !s[g.sector] }))}
                        className="w-full flex items-center justify-between gap-3 px-5 py-3.5 hover:bg-[#FAFAFB] transition-colors text-left"
                      >
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <ChevIcon className="w-4 h-4 text-[#6B7280] shrink-0" />
                          <span className="font-semibold text-[13.5px] text-[#111827] truncate min-w-[120px] max-w-[240px]">{g.sector}</span>
                          {/* Mini bar chart — visual weight of this niche */}
                          <div className="hidden sm:block flex-1 max-w-[300px] h-2 bg-[#F3F4F6] rounded-full overflow-hidden" data-testid={`captured-bar-${g.sector}`}>
                            <div className="h-full rounded-full bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] transition-all" style={{ width: `${barPct}%` }} />
                          </div>
                          <span className="inline-flex items-center justify-center min-w-[26px] h-6 px-2 rounded-full bg-[#EFF6FF] text-[#3B82F6] text-[11.5px] font-bold shrink-0">
                            {g.count}
                          </span>
                        </div>
                        <span className="text-[11.5px] text-[#3B82F6] font-medium shrink-0">
                          {open ? t("dash.captured_hide") : t("dash.captured_show")}
                        </span>
                      </button>

                      {open && (
                        <div className="px-5 pb-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2.5">
                          {g.leads.map((ld, idx) => {
                            const isEnriching = enrichingId === ld.id;
                            const hasWebsite = !!ld.website;
                            const src = sourceMeta(ld);
                            const wa = waLink(ld);
                            const isMatch = claireMatch(ld);
                            return (
                            <div
                              key={`${g.sector}-${ld.id || ld.name}-${idx}`}
                              data-testid={`captured-lead-${ld.id || ld.name}`}
                              role="button" tabIndex={0}
                              onClick={() => navigate(`/kanban?q=${encodeURIComponent(ld.name || "")}`)}
                              onKeyDown={(e) => { if (e.key === "Enter") navigate(`/kanban?q=${encodeURIComponent(ld.name || "")}`); }}
                              title={lang === "pt" ? "Clique para trabalhar este lead no CRM Kanban" : "Click to work this lead in the CRM Kanban"}
                              className={
                                "border rounded-lg p-3 bg-white cursor-pointer transition-all hover:shadow-md hover:-translate-y-[1px] " +
                                (isMatch
                                  ? "border-[#F59E0B] ring-2 ring-[#F59E0B]/30 shadow-[0_0_12px_rgba(245,158,11,0.25)]"
                                  : "border-[#E5E7EB] hover:border-[#3B82F6]")
                              }
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="font-semibold text-[12.5px] text-[#111827] truncate">{ld.name}</div>
                                <span className={
                                  "text-[10px] font-semibold px-1.5 py-0.5 rounded shrink-0 " +
                                  (ld.badge === "QUENTE" ? "bg-[#FEE2E2] text-[#DC2626]" :
                                   ld.badge === "FRIO" ? "bg-[#DCFCE7] text-[#16A34A]" :
                                   "bg-[#FFEDD5] text-[#C2410C]")
                                }>{ld.badge || "MORNO"}</span>
                              </div>
                              <div className="flex items-center gap-1.5 mt-1">
                                <span data-testid={`lead-source-${ld.id}`}
                                  className="text-[9.5px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide shrink-0"
                                  style={{ background: src.bg, color: src.fg }}>
                                  {src.label}
                                </span>
                                <span className="text-[11.5px] text-[#6B7280] truncate">{ld.address}</span>
                              </div>
                              {isMatch && (
                                <div className="mt-1.5 flex items-center gap-1.5 text-[10.5px] font-semibold text-[#B45309] bg-[#FEF3C7] rounded px-2 py-1" data-testid={`claire-suggest-${ld.id}`}>
                                  <PhoneCall className="w-3 h-3 shrink-0" />
                                  {lang === "pt" ? "Claire sugere ligar agora — match perfeito detectado" : "Claire suggests calling now — perfect match detected"}
                                </div>
                              )}
                              <div className="flex items-center justify-between text-[11px] text-[#6B7280] mt-1.5">
                                <span>{ld.phone_international || ld.phone || "—"}</span>
                                <span className="font-bold text-[#111827]">{ld.digital_score ?? "—"}/100</span>
                              </div>
                              <div className="mt-2 pt-2 border-t border-[#F3F4F6] flex items-center gap-1.5">
                                {wa && (
                                  <a href={wa} target="_blank" rel="noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    data-testid={`lead-wa-${ld.id}`}
                                    title="WhatsApp"
                                    className="w-6 h-6 rounded flex items-center justify-center bg-[#DCFCE7] text-[#16A34A] hover:bg-[#16A34A] hover:text-white transition-colors">
                                    <MessageCircle className="w-3.5 h-3.5" />
                                  </a>
                                )}
                                {hasWebsite && (
                                  <a href={ld.website.startsWith("http") ? ld.website : `https://${ld.website}`}
                                    target="_blank" rel="noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    data-testid={`lead-site-${ld.id}`}
                                    title={lang === "pt" ? "Abrir site" : "Open website"}
                                    className="w-6 h-6 rounded flex items-center justify-center bg-[#EFF6FF] text-[#3B82F6] hover:bg-[#3B82F6] hover:text-white transition-colors">
                                    <Globe className="w-3.5 h-3.5" />
                                  </a>
                                )}
                                <span className="flex-1" />
                                <button
                                  type="button"
                                  data-testid={`reenrich-btn-${ld.id}`}
                                  disabled={isEnriching || !hasWebsite}
                                  onClick={(e) => { e.stopPropagation(); reenrichLead(ld.id, ld.name); }}
                                  title={!hasWebsite ? t("dash.enrich_no_website_tooltip") : t("dash.enrich_button_tooltip")}
                                  className={
                                    "inline-flex items-center gap-1 px-2 h-6 rounded text-[10.5px] font-semibold transition-colors shrink-0 " +
                                    (isEnriching
                                      ? "bg-[#EFF6FF] text-[#3B82F6] cursor-wait"
                                      : !hasWebsite
                                        ? "bg-[#F3F4F6] text-[#9CA3AF] cursor-not-allowed"
                                        : "bg-[#3B82F6] text-white hover:bg-[#2563EB]")
                                  }
                                >
                                  {isEnriching ? (
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                  ) : (
                                    <Sparkles className="w-3 h-3" />
                                  )}
                                  {isEnriching ? t("dash.enrich_running") : "AI Enrich"}
                                </button>
                              </div>
                            </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                  });
                })()}
              </div>
            )}
          </section>

          {/* Free Sources moved to dedicated /free-sources page (sidebar item) */}
        </main>
      </div>
    </div>
  );
}
